/**
 * Layout JSON (SPEC §5.2): camera geometry exported by the builder and imported by
 * the graphics app. A layout holds geometry and an optional nameplate anchor hint.
 * It never holds bindings to OBS items, participants or feeds.
 *
 * Slots are semantic and named (`host`, `guest-1`, `speaker`, `share`, `main` …),
 * never positional indices.
 */
import { z } from 'zod';
import { ObsAlignmentSchema, ObsBoundsTypeSchema } from './obs.ts';
import { findDuplicates, parseOrThrow, ValidationError } from './validate.ts';

/** The single reference canvas every layout is authored in. */
export const REFERENCE_CANVAS = { width: 1920, height: 1080 } as const;

export const LAYOUT_SCHEMA_VERSION = 1;

/** Upper bound on slots per layout; guards against garbage imports. */
export const MAX_LAYOUT_SLOTS = 16;

/** Slot IDs and layout IDs: lowercase kebab-case, e.g. `guest-1`, `4-box`. */
const kebabId = z
  .string()
  .max(64)
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'must be lowercase kebab-case, e.g. "guest-1"');

// Limits match obs-websocket 5.7.4 SetSceneItemTransform validation.
const coord = z.number().finite().min(-90_001).max(90_001);
const size = z.number().finite().min(1).max(90_001);
const crop = z.number().int().min(0).max(100_000);

export const NameplateAnchorSchema = z.enum([
  'inside-bottom-left',
  'inside-bottom-center',
  'below',
  'above',
]);
export type NameplateAnchor = z.infer<typeof NameplateAnchorSchema>;

export const LayoutSlotSchema = z.strictObject({
  slotId: kebabId,
  order: z.number().int().min(0),
  // OBS-compatible transform, in reference-canvas pixels
  positionX: coord,
  positionY: coord,
  alignment: ObsAlignmentSchema,
  boundsType: ObsBoundsTypeSchema,
  boundsWidth: size,
  boundsHeight: size,
  cropLeft: crop,
  cropRight: crop,
  cropTop: crop,
  cropBottom: crop,
  rotation: z.number().finite().min(-360).max(360),
  zIndex: z.number().int(),
  /** A parked slot round-trips through the builder. */
  hidden: z.boolean().optional(),
  nameplate: z
    .strictObject({
      anchor: NameplateAnchorSchema,
      offsetX: coord.optional(),
      offsetY: coord.optional(),
    })
    .optional(),
});
export type LayoutSlot = z.infer<typeof LayoutSlotSchema>;

export const LayoutFileSchema = z
  .strictObject({
    schemaVersion: z.literal(LAYOUT_SCHEMA_VERSION),
    /** File stem, e.g. `4-box`. */
    layoutId: kebabId,
    name: z.string().trim().min(1).max(200),
    canvas: z.strictObject({
      width: z.literal(REFERENCE_CANVAS.width),
      height: z.literal(REFERENCE_CANVAS.height),
    }),
    /** Used for auto-selection by participant count. */
    slotCount: z.number().int().min(1).max(MAX_LAYOUT_SLOTS),
    slots: z.array(LayoutSlotSchema).min(1, 'a layout needs at least one slot').max(MAX_LAYOUT_SLOTS, `a layout has at most ${MAX_LAYOUT_SLOTS} slots`),
  })
  // Cross-slot checks run even when other fields are invalid (`when`), so an import
  // lists every problem at once. The value may be malformed here: read it defensively.
  .check(
    z.superRefine(
      (value, ctx) => {
        const raw = value as { slots?: unknown; slotCount?: unknown };
        const slots = Array.isArray(raw.slots) ? (raw.slots as unknown[]).filter((s): s is Record<string, unknown> => typeof s === 'object' && s !== null) : [];
        const ids = slots.map((s) => s['slotId']).filter((v): v is string => typeof v === 'string');
        for (const dup of findDuplicates(ids)) {
          ctx.addIssue({ code: 'custom', path: ['slots'], message: `duplicate slotId "${dup}"` });
        }
        const orders = slots.map((s) => s['order']).filter((v): v is number => typeof v === 'number');
        for (const dup of findDuplicates(orders)) {
          ctx.addIssue({ code: 'custom', path: ['slots'], message: `duplicate order ${dup}` });
        }
        if (typeof raw.slotCount === 'number' && Array.isArray(raw.slots) && raw.slotCount > raw.slots.length) {
          ctx.addIssue({
            code: 'custom',
            path: ['slotCount'],
            message: `slotCount ${raw.slotCount} exceeds the ${raw.slots.length} slots defined`,
          });
        }
      },
      { when: (payload) => typeof payload.value === 'object' && payload.value !== null },
    ),
  );
export type LayoutFile = z.infer<typeof LayoutFileSchema>;

/**
 * Validates an imported layout (SPEC §5.2: schema version, canvas, unique slot IDs).
 * @param data Parsed JSON.
 * @param fileName When given, `layoutId` must equal the file stem (`4-box.json` → `4-box`).
 * @throws ValidationError listing every problem.
 */
export function parseLayoutFile(data: unknown, fileName?: string): LayoutFile {
  const context = fileName === undefined ? 'layout' : `layout file "${fileName}"`;
  const layout = parseOrThrow(LayoutFileSchema, data, context);
  if (fileName !== undefined) {
    const stem = fileName.replace(/^.*[\\/]/, '').replace(/\.json$/i, '');
    if (layout.layoutId !== stem) {
      throw new ValidationError(context, [
        { path: 'layoutId', message: `"${layout.layoutId}" must match the file stem "${stem}"` },
      ]);
    }
  }
  return layout;
}
